import { type ItineraryPricingPreferencesDTO } from "../types/dtos/api/pricing/itinerary-pricing-preferences.dto";

type SwissReductionCard = NonNullable<ItineraryPricingPreferencesDTO["swissReductionCard"]>;

type CategoryOfService = NonNullable<ItineraryPricingPreferencesDTO["categoryOfService"]>;

/**
 * Normalizes the Swiss reduction card token for API requests.
 *
 * @param data Candidate pricing preferences payload.
 * @returns Supported reduction card value, or `undefined` when absent.
 */
const mapSwissReductionCard = ( data: Pick<ItineraryPricingPreferencesDTO, "swissReductionCard"> ): SwissReductionCard | undefined => {
    if ( data.swissReductionCard !== undefined ) {
        return data.swissReductionCard;
    }

    return undefined;
};

/**
 * Normalizes travel-class preference to CFF category-of-service codes.
 *
 * @param categoryOfService Requested class from UI (`FIRST_CLASS`) or API (`"1"` / `"2"`).
 * @returns `"1"` for first class, `"2"` for second class, or `undefined` when unset.
 */
const mapCategoryOfService = ( categoryOfService: ItineraryPricingPreferencesDTO["categoryOfService"] | "FIRST_CLASS" | null | undefined ): CategoryOfService | undefined => {
    if ( categoryOfService === undefined || categoryOfService === null ) {
        return undefined;
    }

    if ( categoryOfService === "1" || categoryOfService === "FIRST_CLASS" ) {
        return "1";
    }

    if ( categoryOfService === "2" ) {
        return "2";
    }

    return "2";
};

/**
 * Passes through the supersaver preference when explicitly provided.
 *
 * @param value Requested supersaver flag.
 * @returns The same boolean when defined, otherwise `undefined`.
 */
const mapUseSuperSaverIfAvailable = ( value: boolean | undefined ): boolean | undefined => {
    return value;
};

/**
 * Passes through the personal-bike transport preference when explicitly provided.
 *
 * @param value Requested personal-bike flag.
 * @returns The same boolean when defined, otherwise `undefined`.
 */
const mapTransportPersonalBike = ( value: boolean | undefined ): boolean | undefined => {
    return value;
};

/**
 * Maps itinerary pricing preferences to the compact payload sent to pricing and offer APIs.
 *
 * @param data Pricing preferences assembled from persisted UI state or callers.
 * @returns Normalized preferences, or `undefined` when no preference field is set.
 */
export const mapItineraryPricingPreferencesForApi = ( data: ItineraryPricingPreferencesDTO ): ItineraryPricingPreferencesDTO | undefined => {
    const swissReductionCard = mapSwissReductionCard( { "swissReductionCard": data.swissReductionCard } );
    const categoryOfService = mapCategoryOfService( data.categoryOfService );
    const useSuperSaverIfAvailable = mapUseSuperSaverIfAvailable( data.useSuperSaverIfAvailable );
    const transportPersonalBike = mapTransportPersonalBike( data.transportPersonalBike );
    const passengerBirthdate = typeof data.passengerBirthdate === "string" && data.passengerBirthdate !== "" ? data.passengerBirthdate : undefined;

    if (
        swissReductionCard === undefined &&
        categoryOfService === undefined &&
        useSuperSaverIfAvailable === undefined &&
        transportPersonalBike === undefined &&
        passengerBirthdate === undefined
    ) {
        return undefined;
    }

    return {
        ...swissReductionCard !== undefined ? { "swissReductionCard": swissReductionCard } : {},
        ...categoryOfService !== undefined ? { "categoryOfService": categoryOfService } : {},
        ...useSuperSaverIfAvailable !== undefined ? { "useSuperSaverIfAvailable": useSuperSaverIfAvailable } : {},
        ...transportPersonalBike !== undefined ? { "transportPersonalBike": transportPersonalBike } : {},
        ...passengerBirthdate !== undefined ? { "passengerBirthdate": passengerBirthdate } : {},
    };
};
